import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import RouteConstant from '../constants/route.constants';
import LocalStorageConstant from '../constants/localstroage.constant';
import TokenService from '../services/autentication/token.service';
import { CustomerDelivery, DELIVERY_CHANGED_KEY, getCustomerDelivery, getPincodeDelivery, saveDefaultDeliveryAddress } from '../services/products/customer-delivery.service';
import { CurrentLocationIssue, CurrentLocationPincode, currentLocationIssueMessage, getCurrentLocationPincode } from '../services/products/current-location.service';

interface DeliveryState {
  userId: number | null;
  data: CustomerDelivery | null;
  loading: boolean;
  error: string | null;
  isHomePage: boolean;
  availabilityPending: boolean;
  locating: boolean;
  currentLocation: CurrentLocationPincode | null;
  locationFallbackId: number | null;
  locationIssue: CurrentLocationIssue | null;
  selectCurrentLocation: () => Promise<void>;
  refresh: () => Promise<CustomerDelivery | null>;
  selectAddress: (id: number) => Promise<void>;
  selectPincode: (pincode: string) => Promise<void>;
}
interface Snapshot { ownerId: number | null; data: CustomerDelivery | null; loading: boolean; error: string | null;
  locating: boolean; currentLocation: CurrentLocationPincode | null; locationFallbackId: number | null; locationIssue: CurrentLocationIssue | null }
interface LocationChoice { ownerId: number | null; mode: 'automatic' | 'manual'; controller: AbortController;
  lookup?: Promise<CurrentLocationPincode | null>; applied: boolean; id: number; issue?: CurrentLocationIssue }
const initial: Snapshot = { ownerId: null, data: null, loading: false, error: null, locating: false, currentLocation: null, locationFallbackId: null, locationIssue: null };
const noPincode = (): CustomerDelivery => ({ selectedAddressId: null, addresses: [], usesPosStock: false,
  outOfStockProductIds: [], pincode: null, requiresPincode: true });
const DeliveryContext = createContext<DeliveryState>({
  userId: null, data: null, loading: false, error: null, isHomePage: false, availabilityPending: false, locating: false, currentLocation: null, locationFallbackId: null, locationIssue: null,
  selectCurrentLocation: async () => { throw new Error('Current location is unavailable.'); },
  refresh: async () => null, selectAddress: async () => { throw new Error('Delivery selection is unavailable.'); },
  selectPincode: async () => { throw new Error('Delivery selection is unavailable.'); }
});
const readOwnerId = (): number | null => {
  try { const id = Number(TokenService.getUser()?.userID); return Number.isSafeInteger(id) && id > 0 ? id : null; }
  catch { return null; }
};
const pincodeKey = (owner: number | null) => `delivery-pincode-v1:${process.env.REACT_APP_TENAT_ID || 'default'}:${owner ?? 'guest'}`;
const savedPincode = (owner: number | null) => {
  try { const pin = localStorage.getItem(pincodeKey(owner)) || ''; return /^[1-9][0-9]{5}$/.test(pin) ? pin : null; }
  catch { return null; }
};
const signalChange = () => { try { localStorage.setItem(DELIVERY_CHANGED_KEY, Date.now().toString()); } catch { } };
const normalize = (path: string) => path.replace(/\/+$/, '') || '/';

export function CustomerDeliveryProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const userId = readOwnerId();
  const isHomePage = normalize(location.pathname) === normalize(RouteConstant.products);
  const [snapshot, setSnapshot] = useState<Snapshot>(initial);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0), writing = useRef(false), mounted = useRef(true);
  const choiceSequence = useRef(0);
  const choice = useRef<LocationChoice>({ ownerId: userId, mode: 'automatic', controller: new AbortController(), applied: false, id: 0 });
  const resetChoice = useCallback((ownerId: number | null, mode: LocationChoice['mode']) => {
    choice.current.controller.abort();
    choice.current = { ownerId, mode, controller: new AbortController(), applied: false, id: ++choiceSequence.current };
  }, []);

  const refresh = useCallback(async (): Promise<CustomerDelivery | null> => {
    if (writing.current) return null;
    const ownerId = readOwnerId(), requestId = ++sequence.current;
    if (choice.current.ownerId !== ownerId) resetChoice(ownerId, 'automatic');
    const selection = choice.current;
    const isCurrent = () => mounted.current && requestId === sequence.current && readOwnerId() === ownerId && choice.current === selection;
    setSnapshot(old => ({ ...initial, ownerId, data: old.ownerId === ownerId ? old.data : null,
      currentLocation: old.ownerId === ownerId ? old.currentLocation : null, loading: true }));
    let locationFallbackId: number | null = null;
    try {
      let data = ownerId ? await getCustomerDelivery() : noPincode();
      if (!isCurrent()) return null;
      let currentLocation: CurrentLocationPincode | null = null;
      if (selection.mode === 'automatic') {
        // Share one attempt across focus/navigation refreshes. The user can still
        // choose an own address while location is pending; that aborts this choice.
        if (!selection.applied) setSnapshot({ ...initial, ownerId, data, locating: true });
        selection.lookup ??= getCurrentLocationPincode(selection.controller.signal, issue => { selection.issue = issue; });
        const found = await selection.lookup;
        if (!isCurrent()) return null;
        if (found) {
          try {
            const availability = await getPincodeDelivery(found.pincode);
            if (!isCurrent()) return null;
            // Keep private address/default metadata for the selector and checkout.
            // Only browsing availability/label uses the detected pincode.
            data = { ...availability, addresses: data.addresses, selectedAddressId: data.selectedAddressId };
            currentLocation = found;
          } catch (failure) {
            if (!isCurrent()) return null;
            if (selection.applied) throw failure;
            // An initial lookup that cannot be verified retains the original flow.
            selection.lookup = Promise.resolve(null);
            selection.issue = 'availability-unavailable';
          }
        }
        if (!currentLocation) locationFallbackId = selection.id;
      }
      const pin = savedPincode(ownerId);
      if (!currentLocation && data.addresses.length === 0 && pin) data = await getPincodeDelivery(pin);
      if (isCurrent()) {
        selection.applied = currentLocation !== null;
        setSnapshot({ ...initial, ownerId, data, currentLocation, locationFallbackId, locationIssue: currentLocation ? null : selection.issue ?? null });
        return data;
      }
    } catch {
      if (isCurrent()) setSnapshot(old => ({ ...old, loading: false, locating: false, locationFallbackId,
        error: 'Could not check delivery availability. Please retry.' }));
    }
    return null;
  }, [resetChoice]);

  const selectCurrentLocation = useCallback(async () => {
    const ownerId = readOwnerId();
    if (writing.current) throw new Error('Delivery selection is in progress. Please retry.');
    resetChoice(ownerId, 'automatic');
    const selection = choice.current;
    const data = await refresh();
    if (choice.current !== selection || readOwnerId() !== ownerId) return;
    if (!data) throw new Error('Could not check delivery availability. Please retry or choose a saved address.');
    if (!selection.applied) throw new Error(currentLocationIssueMessage(selection.issue));
  }, [refresh, resetChoice]);

  const selectAddress = useCallback(async (id: number) => {
    const ownerId = readOwnerId();
    if (!ownerId || writing.current) throw new Error('Delivery selection is unavailable.');
    resetChoice(ownerId, 'manual');
    writing.current = true;
    ++sequence.current;
    setSnapshot(old => ({ ...initial, ownerId, data: old.ownerId === ownerId ? old.data : null, loading: true }));
    let saved = false;
    try {
      await saveDefaultDeliveryAddress(id);
      saved = true;
      if (!mounted.current || readOwnerId() !== ownerId) return;
      signalChange();
      writing.current = false;
      if (!await refresh()) throw new Error('Availability reload failed.');
    } catch {
      const message = saved ? 'Address saved, but availability could not be refreshed. Please retry.' :
        'Could not confirm the address change. Refresh availability before continuing.';
      if (mounted.current && readOwnerId() === ownerId) setSnapshot(old => ({ ...old, loading: false, error: message }));
      throw new Error(message);
    } finally {
      writing.current = false;
      if (mounted.current && readOwnerId() !== ownerId) void refresh();
    }
  }, [refresh, resetChoice]);

  const selectPincode = useCallback(async (pincode: string) => {
    const ownerId = readOwnerId();
    if (writing.current || (snapshotRef.current.ownerId === ownerId && snapshotRef.current.data?.addresses.length))
      throw new Error('Choose a saved address or update its pincode.');
    if (ownerId && (snapshotRef.current.ownerId !== ownerId || !snapshotRef.current.data || snapshotRef.current.error))
      throw new Error('Load your saved addresses before checking a pincode. Please retry availability.');
    const pin = pincode.trim();
    if (!/^[1-9][0-9]{5}$/.test(pin)) throw new Error('Enter a valid six-digit pincode.');
    resetChoice(ownerId, 'manual');
    writing.current = true;
    const requestId = ++sequence.current;
    setSnapshot(old => ({ ...initial, ownerId, data: old.ownerId === ownerId ? old.data : null, loading: true }));
    try {
      const data = await getPincodeDelivery(pin);
      if (!mounted.current || requestId !== sequence.current || readOwnerId() !== ownerId) return;
      try { localStorage.setItem(pincodeKey(ownerId), pin); } catch { /* Remembering a checked pin is optional. */ }
      signalChange();
      setSnapshot({ ...initial, ownerId, data });
    } catch {
      const message = 'Could not check this pincode. Please retry.';
      if (mounted.current && readOwnerId() === ownerId) setSnapshot(old => ({ ...old, loading: false, error: message }));
      throw new Error(message);
    } finally {
      writing.current = false;
      if (mounted.current && readOwnerId() !== ownerId) void refresh();
    }
  }, [refresh, resetChoice]);

  useEffect(() => {
    mounted.current = true;
    if (choice.current.controller.signal.aborted) resetChoice(readOwnerId(), choice.current.mode);
    return () => { mounted.current = false; choice.current.controller.abort(); };
  }, [resetChoice]);
  useEffect(() => {
    if (choice.current.ownerId !== userId) { resetChoice(userId, 'automatic'); ++sequence.current; }
  }, [userId, resetChoice]);
  useEffect(() => {
    const revisit = () => setRevision(v => v + 1);
    const focus = () => { if (!snapshotRef.current.locating) revisit(); };
    const storage = (event: StorageEvent) => {
      if (event.key === DELIVERY_CHANGED_KEY) resetChoice(readOwnerId(), 'manual');
      if (event.key === null || event.key === LocalStorageConstant.user || event.key === DELIVERY_CHANGED_KEY || event.key === 'pincodePosMappingsChanged' || event.key === pincodeKey(readOwnerId())) revisit();
    };
    window.addEventListener('focus', focus); window.addEventListener('storage', storage); window.addEventListener('pincodePosMappingsChanged', revisit);
    return () => { window.removeEventListener('focus', focus); window.removeEventListener('storage', storage); window.removeEventListener('pincodePosMappingsChanged', revisit); };
  }, [resetChoice]);
  useEffect(() => { if (isHomePage) void refresh(); }, [userId, isHomePage, location.key, revision, refresh]);

  const ownsSnapshot = snapshot.ownerId === userId;
  const data = ownsSnapshot ? snapshot.data : null;
  const error = ownsSnapshot ? snapshot.error : null;
  const loading = isHomePage && (!ownsSnapshot || snapshot.loading || (!data && !error));
  const locating = ownsSnapshot && snapshot.locating;
  const currentLocation = ownsSnapshot ? snapshot.currentLocation : null;
  const locationFallbackId = ownsSnapshot ? snapshot.locationFallbackId : null;
  const locationIssue = ownsSnapshot ? snapshot.locationIssue : null;
  const availabilityPending = isHomePage && (loading || locating || error !== null || data === null || data.requiresPincode === true);
  return <DeliveryContext.Provider value={{ userId, data, loading, error, isHomePage, availabilityPending, locating, currentLocation, locationFallbackId, locationIssue,
    refresh, selectAddress, selectPincode, selectCurrentLocation }}>
    {children}
  </DeliveryContext.Provider>;
}
export const useCustomerDelivery = () => useContext(DeliveryContext);
